import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { flushSync } from 'react-dom';
import { Box } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';

import { FHButton, FProgressBar } from '../../../../base';
import { goalActions, treatmentPlanActions } from '../../../../../store';
import { useTreatmentPlanEditable } from '../../hooks/useTreatmentPlanEditable';
import GoalItem from './GoalItem';
import { goalSchema } from './validationSchema';
import { validateSchema } from '../../../../../helpers/validation-helpers';
import { FORTA_WHITE } from '../../../../../utils/colors';
import { Themes } from '../../../../../constants';
import { TreatmentPlanPages } from '../../constants/general';
import { GOAL_DETAILS_FIELD_IDS } from '../common/constants/general';
import {
  GoalMeasurementDurationUnit,
  GoalTypes,
  initialTreatmentPlanGoal,
  ThresholdTypes,
  TreatmentPlanGoalStatus,
} from './constants';
import PlusIcon from '../../../../svgs/PlusIcon';

import type {
  IHypothesizedFunction,
  ITreatmentPlanGoal,
  ITreatmentPlanGoalTarget,
} from '../../../../../utils/types';

interface GoalsContainerProps {
  type: GoalTypes;
}

function GoalsContainer(props: GoalsContainerProps) {
  const { type = GoalTypes.BehaviorReduction } = props;

  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const isEditable = useTreatmentPlanEditable();
  const { current: behavior } = useSelector((state: any) => state.behaviors);
  const { current: treatmentPlan } = useSelector((state: any) => state.treatmentPlans);
  const {
    loading: goalsLoading,
    behaviorReductionGoals,
    skillAcquisitionGoals,
    caregiverGoals,
    validationErrors,
  } = useSelector((state: any) => state.goals);
  const { list: symptoms } = useSelector((state: any) => state.symptoms);
  const [submittings, setSubmittings] = useState<{ [key: number]: boolean } | null>(null);
  const [targetSaving, setTargetSaving] = useState<boolean>(false);
  const goalsRef = useRef<HTMLDListElement | null>(null);
  const [goalList, setGoalList] = useState<ITreatmentPlanGoal[]>([]);
  const goalListRef = useRef(goalList);
  const [activeSkillAcquisitionGoals, setActiveSkillAcquisitionGoals] = useState<
    ITreatmentPlanGoal[]
  >([]);
  const [activeCaregiverGoals, setActiveCaregiverGoals] = useState<ITreatmentPlanGoal[]>([]);

  useEffect(() => {
    if (!treatmentPlan || !type) return;

    switch (type) {
      case GoalTypes.BehaviorReduction:
        setGoalList(behaviorReductionGoals || []);
        break;
      case GoalTypes.SkillAcquisition:
        setGoalList(skillAcquisitionGoals || []);
        break;
      case GoalTypes.Caregiver:
        setGoalList(caregiverGoals || []);
        break;
      default:
        setGoalList([]);
        break;
    }

    setActiveSkillAcquisitionGoals(
      skillAcquisitionGoals?.filter((goal: ITreatmentPlanGoal) => goal.is_active) || [],
    );
    setActiveCaregiverGoals(
      caregiverGoals?.filter((goal: ITreatmentPlanGoal) => goal.is_active) || [],
    );
  }, [treatmentPlan, type, behaviorReductionGoals, skillAcquisitionGoals, caregiverGoals]);

  useEffect(() => {
    goalListRef.current = goalList;
  }, [goalList]);

  useEffect(() => {
    if (!treatmentPlan || !validationErrors || Object.keys(validationErrors).length === 0) {
      dispatch(treatmentPlanActions.updateBadge({ [TreatmentPlanPages.Goals]: 0 }));
      return;
    }

    let errorCount = 0;
    Object.keys(validationErrors).forEach((goalType) => {
      Object.keys(validationErrors[goalType]).forEach((index) => {
        if (validationErrors[goalType][index]) {
          errorCount += Object.keys(validationErrors[goalType][index]).filter((key) => {
            return validationErrors[goalType][index][key];
          }).length;
        }
      });
    });
    // Totally 2 active goals are required at least.
    // One active goal in skill acquisition
    // One active goal in Parent/caregiver
    if (activeSkillAcquisitionGoals.length === 0) {
      errorCount++;
    }
    if (activeCaregiverGoals.length === 0) {
      errorCount++;
    }

    dispatch(treatmentPlanActions.updateBadge({ [TreatmentPlanPages.Goals]: errorCount }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentPlan, validationErrors, activeSkillAcquisitionGoals, activeCaregiverGoals]);

  const validateGoal = (goal: ITreatmentPlanGoal, showAlerts = true) => {
    if (!goal) {
      if (showAlerts) {
        toast.error('Goal not found.');
      }
      return;
    }

    if (!goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME_ID]) {
      if (showAlerts) {
        toast.error('Focus area and short term goal fields are required.');
      }
      return;
    }

    if (
      goal.goal_type === GoalTypes.BehaviorReduction &&
      goal.hypothesized_functions?.length === 0
    ) {
      if (showAlerts) {
        toast.error('Hypothesized function field is required.');
      }
      return;
    }

    if (
      goal.goal_type !== GoalTypes.BehaviorReduction &&
      (!goal.description || goal.description?.length < 10)
    ) {
      if (showAlerts) {
        toast.error('Description requires at least 10 characters.');
      }
      return;
    }

    if (
      goal.goal_type === GoalTypes.BehaviorReduction &&
      (!goal.operational_definition || goal.operational_definition?.length < 10)
    ) {
      if (showAlerts) {
        toast.error('Operational definition requires at least 10 characters.');
      }
      return;
    }

    if (
      goal.goal_type === GoalTypes.BehaviorReduction &&
      (!goal.objective || goal.objective?.length < 10)
    ) {
      if (showAlerts) {
        toast.error('Objective requires at least 10 characters.');
      }
      return;
    }

    if (
      goal.goal_type === GoalTypes.BehaviorReduction &&
      (!goal.antecedent_modifications || goal.antecedent_modifications.length < 10)
    ) {
      if (showAlerts) {
        toast.error('Antecedent modifications requires at least 10 characters.');
      }
      return;
    }

    if (
      goal.goal_type === GoalTypes.BehaviorReduction &&
      (!goal.consequence_modifications || goal.consequence_modifications.length < 10)
    ) {
      if (showAlerts) {
        toast.error('Consequence modifications requires at least 10 characters.');
      }
      return;
    }

    if (goal.goal_type === GoalTypes.BehaviorReduction && !goal.target_mastery) {
      if (showAlerts) {
        toast.error('Target mastery field is required.');
      }
      return;
    }

    if (goal.targets.length === 0) {
      const message =
        goal.goal_type === GoalTypes.BehaviorReduction
          ? 'Goal should have at least one replacement behavior.'
          : 'Goal should have at least one target.';
      if (showAlerts) {
        toast.error(message);
      }
      return;
    }

    if (!goal.id && goal.targets?.some((target) => target.label.length < 2)) {
      if (showAlerts) {
        toast.error('Target requires at least 2 characters.');
      }
      return;
    }

    const goalNameIds = [
      ...behaviorReductionGoals,
      ...skillAcquisitionGoals,
      ...caregiverGoals,
    ].map((goal: ITreatmentPlanGoal) => goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME_ID]);
    const isGoalDuplicate = goalNameIds?.some((id, index) => goalNameIds?.indexOf(id) !== index);
    if (isGoalDuplicate) {
      if (showAlerts) {
        toast.error('Goal with same goal name already exists.');
      }
      return;
    }

    if (goal.targets?.length > 0) {
      const labels = goal.targets?.map((target) => target.label);
      const isTargetDuplicate = labels?.some((label, index) => labels.indexOf(label) !== index);
      if (isTargetDuplicate) {
        if (showAlerts) {
          toast.error('Target duplicated.');
        }
        return;
      }
    }

    return true;
  };

  const fetchSaveGoal = async (goal: ITreatmentPlanGoal) => {
    if (!goal || !treatmentPlan) return;

    const data = {
      ...goal,
      goal_type: type,
      baseline_at_creation:
        goal.baseline_at_creation === 'will_determine' ? null : goal.baseline_at_creation,
      current_level_at_creation:
        goal.current_level_at_creation === 'will_determine' ? null : goal.current_level_at_creation,
      status_at_creation:
        goal.status_at_creation === TreatmentPlanGoalStatus['New goal']
          ? TreatmentPlanGoalStatus['Not introduced']
          : goal.status_at_creation,
    };

    let response;
    if (Object.hasOwn(data, 'id')) {
      // update existing goal
      response = await dispatch(
        goalActions.updateGoal({
          treatmentPlanId: treatmentPlan.id,
          data,
          getAccessTokenSilently,
        }),
      );
    } else {
      // create new goal
      response = await dispatch(
        goalActions.createGoal({
          treatmentPlanId: treatmentPlan.id,
          data,
          getAccessTokenSilently,
        }),
      );
    }

    return response;
  };

  const fetchCreateTarget = async (goalId: number, target: ITreatmentPlanGoalTarget) => {
    if (!treatmentPlan || !goalId || !target) return;

    const res = await dispatch(
      goalActions.createGoalTarget({
        treatmentPlanId: treatmentPlan.id,
        goalId,
        data: target,
        getAccessTokenSilently,
      }),
    );

    if (!res || res.error) return;

    return res.payload;
  };

  const fetchUpdateTarget = async (goalId: number, target: ITreatmentPlanGoalTarget) => {
    if (!treatmentPlan || !goalId || !target) return;

    const res = await dispatch(
      goalActions.updateGoalTarget({
        treatmentPlanId: treatmentPlan.id,
        goalId,
        data: target,
        getAccessTokenSilently,
      }),
    );

    if (!res || res.error) return;

    return res.payload;
  };

  const fetchSaveTargets = async (goalId: number, targets: ITreatmentPlanGoalTarget[]) => {
    if (!goalId || !targets) return [];

    return await Promise.all(
      targets.map(async (target: ITreatmentPlanGoalTarget, index: number) => {
        let res;
        if (target.id) {
          res = await fetchUpdateTarget(goalId, target);
        } else {
          res = await fetchCreateTarget(goalId, { ...target, order: index });
        }
        return res;
      }),
    );
  };

  const handleAddNewGoal = () => {
    if (!behavior) {
      toast.error("You don't have any behavior to add new goal");
      return;
    }

    let newGoal = {
      ...initialTreatmentPlanGoal,
      goal_name: {
        behavior_id: '',
        label: '',
      },
      goal_name_id: '',
      goal_type: type,
      measurement: {
        comparand_type:
          type === GoalTypes.BehaviorReduction ? ThresholdTypes.Number : ThresholdTypes.Percent,
      },
    };

    if (type === GoalTypes.BehaviorReduction) {
      newGoal = {
        ...newGoal,
        operational_definition: '',
        objective: '',
        antecedent_modifications: '',
        consequence_modifications: '',
        hypothesized_functions: [],
        target_mastery: 0,
      };
    } else {
      newGoal = {
        ...newGoal,
        description: '',
      };
    }

    flushSync(() => {
      setGoalList((prev) => [...prev, newGoal]);
    });
    goalsRef.current?.lastElementChild?.scrollIntoView({ behavior: 'smooth' });

    dispatch(
      goalActions.updateGoals({
        type,
        list: [...goalList, newGoal],
      }),
    );
  };

  const handleUpdateGoalField = (index: number, key: string, value: any) => {
    validateAndSetErrors({ ...goalList[index], [key]: value }, index, false);

    const dirty =
      key === 'is_active' || key === 'targets'
        ? false
        : goalList[index].dirty ||
          Object.entries(goalList[index]).find(([field]) => field === key)?.[1] !== value;
    if (key === 'duration_unit') {
      dispatch(
        goalActions.updateGoals({
          type,
          list: goalList.map((goal: ITreatmentPlanGoal, i: number) =>
            index === i
              ? {
                  ...goal,
                  [key]: value,
                  dirty,
                  duration_count:
                    value === GoalMeasurementDurationUnit['Treatment Plan'] ? null : 1,
                }
              : goal,
          ),
        }),
      );
    } else if (key === GOAL_DETAILS_FIELD_IDS.IS_MALADAPTIVE_CORRECTION && symptoms.length > 0) {
      dispatch(
        goalActions.updateGoals({
          type,
          list: goalList.map((goal: ITreatmentPlanGoal, i: number) =>
            index === i
              ? {
                  ...goal,
                  [key]: value,
                  maladaptive_symptom_id: value ? symptoms[0].id : null,
                  dirty,
                }
              : goal,
          ),
        }),
      );
    } else if (key === 'is_active') {
      dispatch(
        goalActions.updateGoals({
          type,
          list: goalList.map((goal: ITreatmentPlanGoal, i: number) =>
            index === i
              ? {
                  ...goal,
                  [key]: value,
                  dirty,
                  targets: goal.targets.map((target) => ({
                    ...target,
                    is_active: value ? true : false,
                  })),
                }
              : goal,
          ),
        }),
      );
    } else {
      dispatch(
        goalActions.updateGoals({
          type,
          list: goalList.map((goal: ITreatmentPlanGoal, i: number) =>
            index === i ? { ...goal, [key]: value, dirty } : goal,
          ),
        }),
      );
    }
  };

  const validateAndSetErrors = (data: ITreatmentPlanGoal, index: number, showAlerts = true) => {
    const isValid = validateGoal(data, showAlerts);

    let errors: any = {};
    const { dirty: _dirty, hypothesized_functions: _hypothesized_functions, ...rest } = data;
    Object.keys(rest).forEach((key) => {
      const isValid = validateSchema(goalSchema, key, rest);
      errors = {
        ...errors,
        [key]: !isValid,
      };
    });
    errors['valid'] = !isValid;
    dispatch(
      goalActions.updateState({
        key: 'validationErrors',
        value: {
          ...validationErrors,
          [type]: {
            ...validationErrors?.[type],
            [index]: errors,
          },
        },
      }),
    );
    return isValid;
  };

  const handleSaveGoal = async (index: number) => {
    const goalList = goalListRef.current;
    const data = { ...goalList[index] };
    const isValid = validateAndSetErrors(data, index, true);
    if (!isValid) {
      return;
    }

    setSubmittings((prev) => ({ ...prev, [index]: true }));
    const { payload } = await fetchSaveGoal(data);
    if (!payload) {
      setSubmittings((prev) => ({ ...prev, [index]: false }));
      return;
    }
    const goal = {
      ...payload,
      targets: payload.targets
        ? payload.targets.sort(
            (a: ITreatmentPlanGoalTarget, b: ITreatmentPlanGoalTarget) =>
              a.order - b.order,
          )
        : [],
    };

    let hypothesizedFunction: IHypothesizedFunction;
    let targets: ITreatmentPlanGoalTarget[] = [];
    if (!data.id && data.targets.length > 0 && goal.id !== undefined) {
      targets = await fetchSaveTargets(goal.id, data.targets);
    }

    if (!data.id && data.hypothesized_functions && data.hypothesized_functions.length > 0) {
      const response = await dispatch(
        goalActions.createHypothesizedFunctionRecord({
          getAccessTokenSilently,
          treatmentPlanId: treatmentPlan?.id,
          goalId: goal.id,
          data: { ...data.hypothesized_functions[0], date_selected: goal.date_introduced },
        }),
      );
      if (!response.error) {
        hypothesizedFunction = response.payload;
      }
    }

    dispatch(
      goalActions.updateGoals({
        type,
        list: goalList.map((prev: ITreatmentPlanGoal, i: number) =>
          index === i
            ? targets.length > 0
              ? {
                  ...goal,
                  targets,
                  dirty: false,
                  hypothesized_functions: data.hypothesized_functions?.[0]?.id
                    ? data.hypothesized_functions
                    : hypothesizedFunction
                      ? [hypothesizedFunction]
                      : [],
                }
              : {
                  ...goal,
                  dirty: false,
                  hypothesized_functions: data.hypothesized_functions?.[0]?.id
                    ? data.hypothesized_functions
                    : hypothesizedFunction
                      ? [hypothesizedFunction]
                      : [],
                }
            : prev,
        ),
      }),
    );

    setSubmittings((prev) => ({ ...prev, [index]: false }));
    toast.success('Goal saved.');
  };

  const handleDeleteGoal = async (index: number) => {
    if (!goalList[index]) {
      toast.error('Goal not found.');
      return;
    }

    setSubmittings((prev) => ({ ...prev, [index]: true }));
    if (goalList[index] != null && Object.hasOwn(goalList[index], 'id')) {
      // call delete api
      const res = await dispatch(
        goalActions.removeGoal({
          treatmentPlanId: treatmentPlan.id,
          goalId: goalList[index].id,
          getAccessTokenSilently,
        }),
      );

      if (!res || res.error) {
        setSubmittings((prev) => ({ ...prev, [index]: false }));
        return;
      }
    }

    const filteredList = goalList.filter((goal: ITreatmentPlanGoal, i: number) => i !== index);
    dispatch(
      goalActions.updateGoals({
        type,
        list: filteredList,
      }),
    );

    let errors: any = {};
    filteredList.forEach((goal, index) => {
      const { dirty: _dirty, hypothesized_functions: _hypothesized_functions, ...rest } = goal;
      Object.keys(rest).forEach((key) => {
        const isValid = validateSchema(goalSchema, key, rest);
        errors = {
          ...errors,
          [index]: {
            ...errors[index],
            [key]: !isValid,
          },
        };
      });
    });

    dispatch(
      goalActions.updateState({
        key: 'validationErrors',
        value: {
          ...validationErrors,
          [type]: errors,
        },
      }),
    );

    setSubmittings((prev) => ({ ...prev, [index]: false }));
    toast.success('Goal deleted.');
  };

  const handleSaveTarget = async (
    goalIndex: number,
    targetIndex: number,
    target: ITreatmentPlanGoalTarget,
  ) => {
    if (!goalList[goalIndex]) return;

    if (!(goalList[goalIndex] != null && Object.hasOwn(goalList[goalIndex], 'id'))) {
      updateTargetByIndex(goalIndex, targetIndex, target);
      return;
    }

    if (target?.label?.length < 2) {
      toast.error('Target requires at least 2 characters.');
      updateTargetByIndex(goalIndex, targetIndex, target);
      return;
    }

    let savedTarget: ITreatmentPlanGoalTarget = target;
    const goalId = goalList[goalIndex].id;
    if (goalId === undefined) return;
    setTargetSaving(true);
    if (Object.hasOwn(target, 'id')) {
      savedTarget = await fetchUpdateTarget(goalId, target);
    } else {
      const lastTargetOrder = Math.max(
        ...goalList[goalIndex].targets
          .filter((target) => !!target.order || target.order === 0)
          .map((target) => target.order),
      );
      savedTarget = await fetchCreateTarget(goalId, {
        ...target,
        order: lastTargetOrder + 1,
      });
    }
    setTargetSaving(false);
    if (!savedTarget) {
      return;
    }

    updateTargetByIndex(goalIndex, targetIndex, savedTarget);
  };

  const updateTargetByIndex = (
    goalIndex: number,
    targetIndex: number,
    target: ITreatmentPlanGoalTarget,
  ) => {
    dispatch(
      goalActions.updateGoals({
        type,
        list: goalList.map((prev: ITreatmentPlanGoal, i: number) =>
          goalIndex === i
            ? {
                ...prev,
                targets: prev.targets.map((t, ti) => (ti === targetIndex ? target : t)),
              }
            : prev,
        ),
      }),
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, width: '100%' }}>
      {goalsLoading && <FProgressBar />}
      <FHButton
        data-testid="addGoalButton"
        startIcon={<PlusIcon color={FORTA_WHITE} />}
        colorTheme={Themes.Primary}
        sx={{ width: 190, height: 40, justifyContent: 'flex-start' }}
        disabled={!isEditable}
        onClick={handleAddNewGoal}
      >
        Add goal
      </FHButton>
      <Box ref={goalsRef}>
        {goalList.map((goal: ITreatmentPlanGoal, index: number) => (
          <GoalItem
            key={index}
            values={goal}
            goalType={type}
            submitting={submittings ? submittings[index] : false}
            targetSaving={targetSaving}
            errors={
              treatmentPlan && validationErrors && validationErrors[type]
                ? validationErrors[type][index]
                : null
            }
            onUpdateField={(key: string, value: any) => handleUpdateGoalField(index, key, value)}
            onSave={() => {
              // Wait until update the targets when click save as soon as on blur the target label
              setTimeout(() => {
                handleSaveGoal(index);
              }, 2000);
            }}
            onDelete={() => handleDeleteGoal(index)}
            onSaveTarget={(targetIndex: number, target: ITreatmentPlanGoalTarget) =>
              handleSaveTarget(index, targetIndex, target)
            }
          />
        ))}
      </Box>
    </Box>
  );
}

export default GoalsContainer;
